<template>
  <div class="portal-page">
    <!-- Header -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
        <h1 class="viva-page-title">MY VIVAS</h1>
      </div>
      <ProfileDropdown
        :student-name="studentName"
        :is-assignment="false"
        current-portal="admissions"
      />
    </div>

    <!-- Main Content -->
    <div class="portal-container">
      <!-- Loading State -->
      <div v-if="loading" class="portal-loading">
        <p>Loading your VIVAs...</p>
      </div>

      <!-- Portal Content -->
      <div v-else class="portal-content">
        <!-- Error Message (shown when API fails, but still allow starting new viva) -->
        <div v-if="loadError" class="viva-card portal-card error-card">
          <div class="card-header">
            <h3 class="error-title">Unable to load your VIVAs</h3>
            <p class="card-description">
              We're having trouble loading your existing VIVAs. You can still start a new VIVA below,
              or try refreshing the page. If the problem persists, please contact
              <a href="mailto:viva@initialview.com">viva@initialview.com</a>.
            </p>
          </div>
        </div>
        <!-- Completed Vivas (ready_to_review) -->
        <div
          v-for="viva in completedVivas"
          :key="viva.unique_id"
          class="viva-card viva-card-green portal-card"
        >
          <div class="card-body">
            <p class="card-description">
              Congratulations! You have completed a VIVA for
            </p>
            <a
              v-if="viva.paper_uid"
              :href="`/player/video/v1/file/${viva.paper_uid}`"
              target="_blank"
              class="paper-link"
            >
              <strong>{{ viva.paper_title }} <i class="fa fa-download"></i> </strong>
            </a>
            <p class="card-info">
              You've made {{ viva.take_count }} recording{{ viva.take_count !== 1 ? 's' : '' }} for this VIVA
            </p>
          </div>
          <div class="card-actions">
            <!-- Sends Table -->
            <div v-if="viva.sends && viva.sends.length > 0" class="sends-section">
              <p class="sends-label">You have sent your VIVA to:</p>
              <table class="sends-table">
                <tbody>
                  <tr v-for="(send, index) in viva.sends" :key="index">
                    <td>{{ send.org_name }}</td>
                    <td class="send-date">{{ formatDate(send.sent_date) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Send button -->
            <button
              class="viva-btn viva-btn-green viva-btn-small"
              @click="navigateToSend(viva.unique_id)"
            >
              {{ viva.sends && viva.sends.length > 0 ? 'Send to more schools!' : 'Send my VIVA to schools!' }}
            </button>
            <!-- Only show redo button if student hasn't sent to any schools yet -->
            <button
              v-if="!viva.sends || viva.sends.length === 0"
              class="viva-btn viva-btn-outline viva-btn-small"
              @click="openRedoModal(viva.unique_id)"
            >
              I'd like to do this VIVA again
            </button>
          </div>
        </div>

        <!-- In-Progress Vivas (ready_to_pay, ready_to_record) -->
        <div
          v-for="viva in inProgressVivas"
          :key="viva.unique_id"
          class="viva-card viva-card-orange portal-card in-progress"
        >
          <div class="card-body">
            <p class="card-description">
              Don't stop now! Finish your VIVA for
            </p>
            <a
              v-if="viva.paper_uid"
              :href="`/player/video/v1/file/${viva.paper_uid}`"
              target="_blank"
              class="paper-link"
            >
              <strong>{{ viva.paper_title }} <i class="fa fa-download"></i> </strong> 
            </a>
          </div>
          <div class="card-actions">
            <button
              class="viva-btn viva-btn-orange viva-btn-small"
              @click="continueViva(viva)"
            >
              Record my VIVA!
          </button>
          </div>
        </div>

        <!-- New VIVA Card (always shown) -->
        <div class="viva-card viva-card-green portal-card">
          <div class="card-body">
            <p class="card-description">
              {{ hasCompletedViva ? 'Ready to do another VIVA?' : 'Ready to do a VIVA?' }}
            </p>
          </div>
          <div class="card-actions">
            <button
              class="viva-btn viva-btn-green viva-btn-small"
              @click="startNewViva"
            >
              Upload a project!
          </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Redo Confirmation Modal -->
    <div v-if="showRedoModal" class="viva-modal-backdrop" @click="closeRedoModal">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Redo your VIVA?</h2>
        </div>
        <div class="viva-modal-body">
          <p>
            Before you proceed, please note:
          </p>
          <ul style="color: var(--viva-grey); font-size: 16px; line-height: 1.8; margin: 0 0 20px 0; padding-left: 25px;">
            <li>You will be charged again for this redo</li>
            <li>Admissions officers will be able to see all of your takes, including your previous recording</li>
          </ul>
          <p>
            Are you sure you want to continue?
          </p>
        </div>
        <div class="viva-modal-footer">
          <button
            class="viva-btn viva-btn-outline"
            @click="closeRedoModal"
            :disabled="redoLoading"
          >
            Cancel
          </button>
          <button
            class="viva-btn viva-btn-orange"
            @click="confirmRedo"
            :disabled="redoLoading"
          >
            {{ redoLoading ? 'Processing...' : 'Redo VIVA' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js'
import ProfileDropdown from '../components/ProfileDropdown.vue'

export default {
  name: 'AdmissionsPortal',
  components: {
    ProfileDropdown
  },
  data() {
    return {
      loading: true,
      loadError: false,
      vivas: [],
      hasCompletedViva: false,
      redoLoading: null,
      showRedoModal: false,
      selectedRedoVivaId: null,
      studentName: ''
    }
  },
  computed: {
    completedVivas() {
      return this.vivas.filter(v => v.student_status === 'ready_to_review')
    },
    inProgressVivas() {
      return this.vivas.filter(v =>
        ['ready_to_pay', 'ready_to_record'].includes(v.student_status)
      )
    }
  },
  async mounted() {
    await this.loadPortalData()
  },
  methods: {
    async loadPortalData() {
      // Clear any information from previous viva sessions
      sessionStorage.removeItem('vparam');
      sessionStorage.removeItem('upload_token');
      try {
        const response = await apiClient.get('/api/viva/student/admissions-portal/')
        if (response.data.status === 200) {
          console.log('Portal data loaded successfully')
          this.vivas = response.data.vivas;
          this.hasCompletedViva = response.data.has_completed_viva;
          this.studentName = response.data.student_name || '';
          this.loadError = false;
        } else {
          // API returned an error status
          console.error('Portal API error:', response.data.error);
          this.loadError = true;
        }
      } catch (error) {
        console.error('Error loading portal data:', error);
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },

    formatDate(isoString) {
      if (!isoString) return '';
      const date = new Date(isoString);
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
      });
    },

    navigateToSend(vivaId) {
      sessionStorage.setItem('vparam', vivaId)
      this.$router.push(`/send?vparam=${vivaId}`)
    },

    continueViva(viva) {
      sessionStorage.setItem('vparam', viva.unique_id)
      if (viva.student_status === 'ready_to_pay') {
        this.$router.push(`/payment?vparam=${viva.unique_id}`)
      } else {
        this.$router.push(`/practice?vparam=${viva.unique_id}`)
      }
    },

    openRedoModal(vivaId) {
      this.selectedRedoVivaId = vivaId
      this.showRedoModal = true
    },

    closeRedoModal() {
      this.showRedoModal = false
      this.selectedRedoVivaId = null
      this.redoLoading = false
    },

    async confirmRedo() {
      if (!this.selectedRedoVivaId) return

      this.redoLoading = true
      try {
        const response = await apiClient.post('/api/viva/student/optional-redo/', {
          viva_unique_id: this.selectedRedoVivaId
        })
        if (response.data.status === 200) {
          sessionStorage.setItem('vparam', this.selectedRedoVivaId)
          this.$router.push(`/payment?vparam=${this.selectedRedoVivaId}`)
          this.closeRedoModal()
        } else {
          alert(response.data.error || 'Failed to initiate redo')
          this.redoLoading = false
        }
      } catch (error) {
        console.error('Error requesting redo:', error)
        alert('Failed to initiate redo. Please try again.')
        this.redoLoading = false
      }
    },

    startNewViva() {
      // Clear vparam to start fresh - backend will create new viva or reuse ready_to_upload
      sessionStorage.removeItem('vparam')
      this.$router.push('/upload')
    }
  }
}
</script>

<style scoped>
@import '../styles/viva-classes.css';

.portal-page {
  background: var(--viva-cream);
  min-height: 100vh;
}

.portal-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 30px;
}

.portal-loading {
  text-align: center;
  padding: 60px;
}

.portal-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.portal-card {
  padding: 30px;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 30px;
  min-height: 0px;
}

.card-header {
  margin-bottom: 20px;
}

.card-description {
  color: var(--viva-grey);
  font-size: 16px;
  margin: 0;
}

.card-body {
  flex: 1;
  text-align: center;
}

.card-info {
  color: var(--viva-grey);
  font-size: 14px;
  /* margin-bottom: 10px; */
}

.paper-link {
  color: var(--viva-orange);
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.paper-link:hover {
  text-decoration: underline;
}

.sends-section {
  margin-top: 20px;
}

.sends-label {
  color: var(--viva-dark);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
}

.sends-table {
  width: 100%;
  border-collapse: collapse;
}

.sends-table td {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  color: var(--viva-grey);
}

.sends-table .send-date {
  text-align: right;
  color: var(--viva-grey);
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  gap: 15px;
  /* margin-top: 20px; */
}

.card-actions .viva-btn {
    min-width: 300px;
  }

/* Error Card */
.error-card {
  border: 2px solid #dc3545;
  background-color: #fff8f8;
}

.error-title {
  color: #dc3545 !important;
}

.error-card a {
  color: var(--viva-orange);
}

/* Responsive */
@media (max-width: 600px) {
  .portal-card {
    flex-direction: column;
    align-items: stretch;
  }

  .card-actions {
    flex-direction: column;
    margin-top: 0;
  }

  .card-actions .viva-btn {
    width: 100%;
  }
}
</style>
